import React, { useState } from 'react';
import { Components } from './styled';
import ProjectCalendar from '../ProjectCalendar';
import { ReactComponent as CalendarIcon } from '../../../assets/icons/icon-calendar-small.svg';
import { mergeDateAndMinutes, parseDateToStringWithFormat } from '../../../utils/dateFormat';
import ProjectTimePicker from '../ProjectTimePicker';
import { calendarConfigs } from '../ProjectCalendar/constants';
import { format } from '../../../utils/dateFormat/types';
import { startOfWeek, endOfWeek } from 'date-fns';

export type ProjectCalendarPickerVariant = 'white' | 'whiteSmall' | 'whiteNoBorders' | 'gray' | 'grayVerySmall';
export type ProjectCalendarPickerDisplayFormat = 'day' | 'week' | 'month';

const ProjectCalendarPicker = ({
  dateValue,
  onDateValueChange,
  timeValue,
  onTimeValueChange,
  handleClose,
  placeholder,
  variant = 'gray',
  valueDisplayFormat = 'day',
  minDate,
  maxDate,
}: {
  dateValue: Date | null;
  onDateValueChange: (value: Date | null) => void;
  timeValue?: number | undefined;
  onTimeValueChange?: (value: number | undefined) => void;
  handleClose?: () => void;
  placeholder?: string;
  variant?: ProjectCalendarPickerVariant;
  valueDisplayFormat?: ProjectCalendarPickerDisplayFormat;
  minDate?: Date;
  maxDate?: Date;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const _handleClose = () => {
    setAnchorEl(null);
    handleClose?.();
  };

  const pickerValue = dateValue
    ? typeof timeValue === 'number'
      ? parseDateToStringWithFormat(mergeDateAndMinutes(dateValue, timeValue) || 0, 'E LLL d, yyyy h:mma')
      : valueDisplayFormat === 'month'
      ? parseDateToStringWithFormat(dateValue, format.monthYear)
      : valueDisplayFormat === 'week'
      ? `${parseDateToStringWithFormat(
          startOfWeek(dateValue, { weekStartsOn: 1 }),
          format.shortMonthDayYear,
        )} — ${parseDateToStringWithFormat(endOfWeek(dateValue, { weekStartsOn: 1 }), format.shortMonthDayYear)}`
      : parseDateToStringWithFormat(dateValue, format.monthDayYear)
    : undefined;

  return (
    <Components.Container>
      <Components.Picker onClick={handleClick} pickerVariant={variant}>
        {dateValue ? (
          <Components.PickerValue>{pickerValue}</Components.PickerValue>
        ) : (
          <Components.PickerPlaceholder>{placeholder || 'Set date'}</Components.PickerPlaceholder>
        )}
        <Components.PickerIconWrapper>
          <CalendarIcon />
        </Components.PickerIconWrapper>
      </Components.Picker>
      <Components.Popover
        open={open}
        anchorEl={anchorEl}
        onClose={_handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <Components.PopoverContent>
          <ProjectCalendar calendarConfig={calendarConfigs.picker(dateValue, onDateValueChange, minDate, maxDate)} />
          {typeof timeValue === 'number' && onTimeValueChange ? (
            <>
              <Components.Separator />
              <ProjectTimePicker value={timeValue} onValueChange={onTimeValueChange} />
            </>
          ) : null}
        </Components.PopoverContent>
      </Components.Popover>
    </Components.Container>
  );
};

export default ProjectCalendarPicker;
